import { INTERVIEW_CANCELED_STATUS, INTERVIEW_OPERATION_TYPE, INTERVIEW_STATUS } from '@/constant/interview'
import { getI18nPropertyValue } from '@/i18n'
import _ from 'lodash'
import moment from 'moment/moment'
import { mapActions, mapGetters } from 'vuex'

export default {
  data () {
    return {
      INTERVIEW_STATUS,
      i18nPrefix: 'personal.interview',
      loading: false,
      cancelDialog: {
        show: false,
        loading: false
      },
      suggestDialog: {
        show: false,
        loading: false
      },
      interviewStatus: null,
      interviewedTime: null,
      availableInterviewTimeList: [],
      interviewerList: [],
      organizer: null,
      jobInfo: null,
      companyInfo: null,
      candidateName: '',
      selectedTime: null
    }
  },
  computed: {
    ...mapGetters('account', ['getCurrentAccount']),
    currentAccount () {
      return this.getCurrentAccount
    },
    interviewUuid () {
      return this.$route.query.interview_uuid
    },
    interviewInfoList () {
      const list = [
        {
          label: this.$t(`${this.i18nPrefix}.label.company`),
          value: this.companyInfo?.name
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.job`),
          value: this.jobInfo?.title
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.organizer`),
          value: this.organizer?.full_name
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.interviewer`),
          value: _.chain(this.interviewerList)
            .map('full_name')
            .join(', ')
            .value()
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.candidate`),
          value: this.candidateName
        },
        {
          hidden: !this.interviewStatus,
          label: this.$t(`${this.i18nPrefix}.label.status`),
          value: this.$t(`admin.application.candidate.label.${_.findKey(INTERVIEW_STATUS, value => value === this.interviewStatus)}`)
        }
      ]
      return _.filter(list, item => !item.hidden)
    },
    interviewTimeLabel () {
      if (this.interviewStatus === INTERVIEW_STATUS.PENDING_CANDIDATE_SELECT_TIME) {
        return this.$t(`${this.i18nPrefix}.label.selectTime`)
      }
      return this.$t(`${this.i18nPrefix}.label.scheduled`)
    },
    showConfirmBtn () {
      return this.interviewStatus === INTERVIEW_STATUS.PENDING_CANDIDATE_SELECT_TIME
    },
    showSuggestBtn () {
      return this.interviewStatus === INTERVIEW_STATUS.PENDING_CANDIDATE_SELECT_TIME ||
        this.interviewStatus === INTERVIEW_STATUS.SCHEDULED
    },
    isInterviewCanceled () {
      return _.includes(INTERVIEW_CANCELED_STATUS, this.interviewStatus)
    },
    isInterviewScheduled () {
      return _.includes([INTERVIEW_STATUS.SCHEDULED, INTERVIEW_STATUS.PENDING_GENERATE_TEAMS_URL, INTERVIEW_STATUS.PENDING_GENERATE_TEAMS_EVENT], this.interviewStatus)
    },
    hiddenBtn () {
      return this.isInterviewCanceled
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('interview', ['getInterviewRecordInfo', 'updateInterviewRecordInfo']),
    async initData () {
      if (!this.interviewUuid) {
        return
      }
      this.loading = true
      try {
        const resp = await this.getInterviewRecordInfo({ interviewUuid: this.interviewUuid })
        if (resp) {
          this.organizer = resp.creator
          this.interviewerList = resp.hire_manager
          this.candidateName = _.get(resp, 'candidate[0].name') || resp.candidate_names || ''
          this.jobInfo = {
            id: resp.job_id,
            get title () {
              return getI18nPropertyValue(resp, 'job_cn_title', 'job_title')
            }
          }
          this.companyInfo = {
            id: resp.company_id,
            get name () {
              return getI18nPropertyValue(resp, 'company_cn_name', 'company_name')
            },
            root_company_id: resp.root_company_id
          }
          this.interviewedTime = {
            startDate: moment(resp.interview_time).format('YYYY-MM-DD'),
            startTime: moment(resp.interview_time).format('HH:mm'),
            endTime: moment(resp.interview_end_time).format('HH:mm')
          }
          this.availableInterviewTimeList = resp.available_interview_time_list
          this.interviewStatus = resp.status
        }
      } finally {
        this.loading = false
      }
    },
    handleSelectInterviewTime (time) {
      this.selectedTime = time
    },
    async handleConfirm () {
      if (!this.selectedTime) {
        this.$message.info(this.$t(`${this.i18nPrefix}.message.selectTime`))
        return
      }
      this.loading = true
      try {
        const params = {
          interviewUuid: this.interviewUuid,
          data: {
            operation_type: INTERVIEW_OPERATION_TYPE.CONFIRM_TIME,
            selectedInterviewTime: this.selectedTime
          }
        }
        const resp = await this.updateInterviewRecordInfo(params)
        if (resp) {
          this.goPersonalPage()
          this.$message.success({
            message: this.$t(`${this.i18nPrefix}.message.confirmSuccess`),
            duration: 5000
          })
          await this.initData()
        }
      } finally {
        this.loading = false
      }
    },
    handleCancelInterview () {
      this.cancelDialog.show = true
    },
    handleSuggestTime () {
      this.suggestDialog.show = true
    },
    async confirmSuggestInterview () {
      this.suggestDialog.loading = true
      try {
        const resp = await this.updateInterviewRecordInfo({
          interviewUuid: this.interviewUuid,
          data: {
            operation_type: INTERVIEW_OPERATION_TYPE.SUGGEST_NEW_TIME
          }
        })
        if (resp) {
          this.goPersonalPage()
          this.$message.success({
            message: this.$t(`${this.i18nPrefix}.message.suggestSuccess`),
            duration: 5000
          })
          this.suggestDialog.show = false
        }
      } finally {
        this.suggestDialog.loading = false
      }
    },
    async confirmCancelInterview () {
      this.cancelDialog.loading = true
      try {
        const resp = await this.updateInterviewRecordInfo({
          interviewUuid: this.interviewUuid,
          data: {
            operation_type: INTERVIEW_OPERATION_TYPE.CANCEL
          }
        })
        if (resp) {
          this.goPersonalPage()
          this.$message.success({
            message: this.$t(`${this.i18nPrefix}.message.cancelSuccess`),
            duration: 5000
          })
          this.cancelDialog.show = false
        }
      } finally {
        this.cancelDialog.loading = false
      }
    },
    goPersonalPage () {
      this.$router.push({
        path: '/recruiting/personal/home',
        query: {
          company_id: this.companyInfo.id
        }
      })
    },
    formatDate (date) {
      const momentDate = moment(date, 'YYYY-MM-DD')
      return momentDate.format('dddd, MMMM DD, YYYY')
    },
    formatDateTime (data, startTime, endTime) {
      const momentStartTime = moment(`${data} ${startTime}`, 'YYYY-MM-DD HH:mm')
      const momentEndTime = moment(`${data} ${endTime}`, 'YYYY-MM-DD HH:mm')
      return `${momentStartTime.format('HH:mm')} - ${momentEndTime.format('HH:mm')}`
    }
  },
  watch: {
    '$i18n.locale': {
      immediate: true,
      handler () {
        const locale = this.$i18n.locale
        if (locale === 'en') {
          moment.locale('en')
        } else {
          moment.locale('zh-cn')
        }
      }
    }
  }
}
